import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import { type AssignedEducation, useMyTechEducation, usePlanTechEducation } from '@/pages/home/useMyTechEducation';
import { useMyWelfare, type EducationTarget } from '@/pages/mypage/useWelfare';

/**
 * 교육 — <b>마이페이지의 탭</b> (사용자 지시 2026-09-22: 「교육은 복지에 있던거를 분리해서 별도
 * 탭으로 가져갈거야」).
 *
 * <h2>왜 복지에서 나왔나</h2>
 * 복지의 다른 것들은 「내가 대상인가 · 얼마를 받나」 를 묻는 자리다. 교육은 <b>내가 해야 할 일</b>이고,
 * 안 하면 회사와 본인이 법으로 걸린다 — 받는 것과 해야 하는 것을 같은 줄에 두면 뒤쪽이 묻힌다.
 *
 * <h2>묶되 섞지 않는다</h2>
 * 위가 전 직원의 <b>법정의무교육</b>(산업안전보건법)이고 아래가 <b>기술인 교육</b>(건설기술진흥법)이다.
 * 사람이 하는 일은 같지만(「들어야 하는 것이 남았는가」) 근거가 다르고, 기술인이 아니면 아래 칸은
 * 아예 서지 않는다.
 *
 * <h2>여기서 증빙을 내지 않는다</h2>
 * 수료증·영수증 제출과 환급 정산은 기술인 교육 화면이 맡는다. 같은 일을 두 화면에 만들면 어느
 * 쪽이 사실인지 아무도 모른다 — 이 탭의 줄은 그 화면으로 가는 링크다.
 */
export function EducationTab() {
  const { data, isPending, error } = useMyWelfare();

  if (isPending) return <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>;
  if (error) return <Notice tone="error">교육 자료를 불러오지 못했습니다.</Notice>;

  return (
    <div className="space-y-5">
      <StatutoryEducation rows={data?.educations ?? []} />
      <TechEducation />
    </div>
  );
}

function StatutoryEducation({ rows }: { rows: EducationTarget[] }) {
  if (rows.length === 0) {
    return (
      <div className="space-y-2">
        <p style={sectionTitle}>법정의무교육</p>
        <Notice tone="info">
          법정의무교육 과정이 아직 등록되지 않았습니다. 인사팀이 기준을 넣으면 여기에 대상 여부가 표시됩니다.
        </Notice>
      </div>
    );
  }

  const pending = rows.filter((row) => !row.done);
  return (
    <div className="space-y-2">
      <p style={sectionTitle}>법정의무교육</p>
      {pending.length > 0 && (
        <Notice tone="warning">
          아직 이수하지 않은 과정이 <b>{pending.length}건</b> 있습니다.
        </Notice>
      )}
      {rows.map((row) => (
        <div key={row.standardId} className="flex items-center gap-3" style={educationRow}>
          <span style={{ fontWeight: 600, minWidth: '160px' }}>{row.name}</span>
          <span style={mutedText}>
            {row.legalBasis ?? ''} {row.hours ? `· ${row.hours}시간` : ''} · {row.cycleMonths}개월 주기
          </span>
          <span className="ml-auto flex items-center gap-2">
            {row.completedOn && <span style={mutedText}>{row.completedOn} 이수</span>}
            {row.done ? (
              <span className="badge badge-green">이수</span>
            ) : (
              <span className="badge badge-yellow">미이수</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * 기술인 교육 — 승급교육 · 계속교육 · 교육비 환급 (DOC-61 §6).
 *
 * <p><b>기술인이 아니면 칸 자체를 세우지 않는다</b>(홈 위젯과 같은 판단 · 사용자 지시 2026-09-16).
 * 받아 보고 나서 판단한다 — 아직 응답이 없을 때 접으면 탭을 열 때마다 이 칸이 깜빡였다 선다.
 */
function TechEducation() {
  const { data, isPending } = useMyTechEducation();

  if (isPending) return null;
  if (!data || !data.technician) return null;

  const notices = [...(data.associationEducations ?? []), ...(data.promotionEducations ?? [])];
  const assigned = data.assigned ?? [];
  const empty =
    notices.length === 0 && assigned.length === 0
    && data.expiries.length === 0 && data.inProgress.length === 0 && data.refunds.length === 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <p style={{ ...sectionTitle, marginBottom: 0 }}>기술인 교육</p>
        <Link to="/approval/draft?formKey=TECH_EDU_APPLY" className="btn-secondary ml-auto" style={compactButton}>
          교육 신청
        </Link>
      </div>

      {empty ? (
        <p style={mutedText}>챙길 교육과 환급이 없습니다.</p>
      ) : (
        <>
          {notices.map((notice) => (
            <p key={notice.key} style={{ fontSize: '13px', color: 'var(--danger)' }}>{notice.message}</p>
          ))}

          {assigned.map((row) => <AssignedRow key={row.id} row={row} />)}

          {data.expiries.map((expiry) => (
            <div key={expiry.educationId} className="flex items-center gap-3" style={educationRow}>
              <span style={{ fontWeight: 600, minWidth: '160px' }}>{expiry.courseName || expiry.label}</span>
              <span style={mutedText}>계속교육 · {expiry.validUntil}까지</span>
              <span className="ml-auto">
                {expiry.expired
                  ? <span className="badge badge-yellow">만료</span>
                  : <span className="badge badge-green">유효</span>}
              </span>
            </div>
          ))}

          {/* 증빙을 낼 차례인 줄은 누르면 그 화면으로 간다 — 「제출하세요」 라고만 하지 않는다. */}
          {data.inProgress.map((pending) => (
            <Link
              key={pending.educationId}
              to={`/hr/tech-education/evidence?education=${pending.educationId}`}
              className="flex items-center gap-3"
              style={educationRow}
            >
              <span style={{ fontWeight: 600, minWidth: '160px' }}>{pending.courseName || pending.label}</span>
              <span style={mutedText}>{pending.institution ?? ''}</span>
              <span className="badge badge-blue ml-auto">증빙 제출</span>
            </Link>
          ))}

          {data.refunds.map((refund) => (
            <div key={refund.educationId} className="flex items-center gap-3" style={educationRow}>
              <span style={{ fontWeight: 600, minWidth: '160px' }}>{refund.courseName || refund.label}</span>
              <span style={mutedText}>
                교육비 환급{refund.fee != null ? ` · ${refund.fee.toLocaleString('ko-KR')}원` : ''}
              </span>
              <span className="ml-auto">
                {refund.refundStatus === 'PAID'
                  ? <span className="badge badge-green">환급 완료</span>
                  : <span className="badge badge-yellow">환급 대기</span>}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * 기술인 관리에서 받은 안내 한 줄 — 들어야 할 것과 내가 적는 완료 예정일 (V564 · 사용자 지시 2026-10-27).
 *
 * <p>칸을 벗어날 때 저장한다. 날짜 칸은 연도를 치는 동안에도 값이 바뀌어(0002-… → 0020-…) 바뀔 때마다
 * 보내면 중간 값이 저장된다. 지난 예정일은 칸 테두리가 붉다.
 */
function AssignedRow({ row }: { row: AssignedEducation }) {
  const plan = usePlanTechEducation();
  const [draft, setDraft] = useState(row.plannedOn ?? '');
  const today = new Date().toLocaleDateString('sv-SE');
  const overdue = !!row.plannedOn && row.plannedOn < today;

  const commit = () => {
    if (draft === (row.plannedOn ?? '') || plan.isPending) return;
    plan.mutate({ id: row.id, plannedOn: draft || null });
  };

  return (
    <div className="flex items-center gap-3" style={educationRow}>
      <span style={{ fontWeight: 600, minWidth: '160px' }}>{row.title}</span>
      <span style={mutedText}>
        {row.sentOn} 안내{row.dueDate ? ` · 기한 ${row.dueDate}` : ''}
      </span>
      <label className="ml-auto flex items-center gap-2" style={mutedText}>
        완료 예정
        <input
          type="date"
          className="input"
          value={draft}
          disabled={plan.isPending}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => event.key === 'Enter' && commit()}
          title={plan.error instanceof Error ? plan.error.message : undefined}
          style={{
            fontSize: '12px',
            padding: '3px 8px',
            borderColor: overdue || plan.isError ? 'var(--danger)' : undefined,
          }}
        />
      </label>
    </div>
  );
}

const educationRow = { padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' } as const;
const compactButton = { fontSize: '12px', padding: '5px 12px' } as const;
const mutedText = { fontSize: '12px', color: 'var(--text-muted)' } as const;
const sectionTitle = { fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' } as const;
